import { create } from 'zustand';
import type { AvatarConfig, UmgebungSummary } from '@etown/shared';
import { api, type SessionUser } from '../net/api';

type SessionStatus = 'loading' | 'loggedOut' | 'loggedIn';

interface SessionState {
  status: SessionStatus;
  user: SessionUser | null;
  error: string | null;
  init: () => Promise<void>;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setAvatar: (avatar: AvatarConfig) => void;
  /** Profil neu laden — nach Aenderungen an Mitgliedschaften oder Rechten. */
  refreshProfile: () => Promise<void>;
}

/** Login-Zustand der Anwendung (Session-Cookie verwaltet der Browser). */
export const useSessionStore = create<SessionState>((set) => ({
  status: 'loading',
  user: null,
  error: null,

  async init() {
    try {
      const { user } = await api.me();
      set({ status: 'loggedIn', user, error: null });
    } catch {
      set({ status: 'loggedOut', user: null });
    }
  },

  async login(username, password) {
    set({ error: null });
    try {
      const { user } = await api.login(username, password);
      set({ status: 'loggedIn', user });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Login fehlgeschlagen' });
    }
  },

  async logout() {
    try {
      await api.logout();
    } finally {
      set({ status: 'loggedOut', user: null });
    }
  },

  setAvatar(avatar) {
    set((state) => (state.user ? { user: { ...state.user, avatar } } : state));
  },

  async refreshProfile() {
    try {
      const { user } = await api.me();
      set({ user });
    } catch {
      set({ status: 'loggedOut', user: null });
    }
  },
}));

/** Die Umgebung mit dieser Kennung aus dem Profil — oder undefined ohne Zugang. */
export function umgebungAus(user: SessionUser | null, umgebungId: string): UmgebungSummary | undefined {
  return user?.umgebungen.find((u) => u.id === umgebungId);
}
